import type { ReactNode } from "react";

import { toneSwatch, toneText, type Tone } from "@/lib/office/status";

/** The office's one save confirmation: a notification that names what was
 *  saved. Creates redirect to the new entity (arriving is the confirmation);
 *  edit-in-place forms and soft actions mount this on their result. Lives in
 *  its own module because it runs on the client; re-exported here so the
 *  `ui` barrel keeps its surface. */
export { SavedNote } from "../SavedNote";

/** The box an empty list leaves: dashed, at the radius of the table or panel
 *  it stands in for. */
export function EmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-line px-6 py-16 text-center">
      <p className="text-sm text-fg-muted">{children}</p>
    </div>
  );
}

/** Status pill. Tone maps to a small fixed palette. */
export function Badge({
  children,
  variant = "neutral",
  className = "",
  title,
}: {
  children: ReactNode;
  variant?: Tone;
  className?: string;
  /** Native tooltip — use to expand a terse pill into a full explanation. */
  title?: string;
}) {
  // A faint tone-tinted fill sets status pills apart from the (fill-less)
  // pill-style action buttons, which share the rounded-full border look.
  // The text colour is the tone's (`toneText`), the one every tone-coloured
  // word in the office reads from; the fill and border are the pill's own.
  // `pt-px pb-0.75`, not `py-0.5`: the mono face's words sit about a pixel
  // under the middle of their line box, and one whole pixel lifts them to the
  // middle of the pill. The same height.
  const fills: Record<Tone, string> = {
    neutral: "bg-fg/5 border-line",
    good: "bg-emerald-500/10 border-emerald-600/40",
    warn: "bg-amber-500/10 border-amber-600/40",
    bad: "bg-red-500/10 border-red-600/40",
    info: "bg-sky-500/10 border-sky-600/40",
    orange: "bg-orange-500/10 border-orange-600/40",
    purple: "bg-purple-500/10 border-purple-600/40",
  };
  return (
    <span
      title={title}
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2.5 pt-px pb-0.75 font-mono text-xs ${fills[variant]} ${toneText(variant)} ${className}`}
    >
      {children}
    </span>
  );
}

/**
 * A result as a dot: green where it went through, red where it did not. For a
 * table column whose rows answer one yes/no — a scan that counted, a check
 * that passed — where a worded pill per row is noise. A dot says nothing a
 * screen reader or a hover can use, so `label` carries the words ("out",
 * "not recorded") as its accessible name and tooltip.
 */
export function ResultMark({ ok, label }: { ok: boolean; label: string }) {
  return <ToneDot variant={ok ? "good" : "bad"} label={label} />;
}

/**
 * A tone as a dot, where a worded pill per row would be noise: the badge's
 * hue with nothing around it. `label` is its accessible name and tooltip,
 * since a dot says nothing a screen reader or a hover can use. `dim` is the
 * same hue stepped back — a mark whose moment has passed (a notification that
 * has been read) — so the column still says what it was. `pulse` counts
 * activity events (a worker's heartbeats landing): each count past zero
 * rings once out of the dot, and none rings on mount, so a page's first
 * paint is still; under reduced motion the ring never shows.
 */
export function ToneDot({
  variant,
  label,
  dim = false,
  pulse,
}: {
  variant: Tone;
  label: string;
  dim?: boolean;
  pulse?: number;
}) {
  const dot = (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={`inline-block h-2 w-2 rounded-full align-middle ${toneSwatch(variant)} ${dim ? "opacity-30" : ""}`.trim()}
    />
  );
  if (!pulse) return dot;
  return (
    <span className="relative inline-flex align-middle">
      {/* Keyed on the count, so each event mounts a fresh ring and its
          one-shot animation plays again. */}
      <span
        key={pulse}
        aria-hidden
        className={`pointer-events-none absolute inset-0 rounded-full ${toneSwatch(variant)} animate-dot-pulse motion-reduce:hidden`}
      />
      {dot}
    </span>
  );
}

/**
 * Centered, bubble-less notice line for the agent chat transcript — the shared
 * style for both persisted system messages (model-change `info`, stream `error`)
 * and the cosmetic agent-state gaps (`warn`: not configured / no model / no
 * prompt). Tone sets the text color; children carry their own inline markup
 * (links, <code>). Mono + small to read as meta, not as a chat turn.
 */
/**
 * How much of something is filled in, as one mark: all of it, some of it, or
 * none of it.
 *
 * A symbol rather than "2 of 3" because the number is only interesting where
 * it is not the whole story — a column of counts reads as arithmetic, where a
 * column of these reads as a state you can scan. The count still belongs
 * wherever a reader has to act on WHICH part is missing.
 *
 * Drawn rather than typed: the half-filled circles in the fonts a browser
 * might reach for are inconsistent at this size, and a ring with a filled
 * half is unambiguous at any of them.
 */
export function UsageDot({
  level,
  title,
}: {
  level: "full" | "partial" | "empty";
  /** What the mark means, for a pointer and for a screen reader. */
  title: string;
}) {
  return (
    <svg
      viewBox="0 0 12 12"
      role="img"
      aria-label={title}
      className="inline-block h-3 w-3 align-middle text-fg"
    >
      <title>{title}</title>
      {level === "partial" && (
        // The left half, drawn as its own arc: a clipped circle would need a
        // second element to clip against, and this is one path.
        <path d="M6 1.5A4.5 4.5 0 0 0 6 10.5Z" fill="currentColor" />
      )}
      <circle
        cx="6"
        cy="6"
        r="4.5"
        fill={level === "full" ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

export function SystemNotice({
  children,
  variant = "info",
}: {
  children: ReactNode;
  variant?: "info" | "warn" | "error";
}) {
  const color =
    variant === "error"
      ? "text-red-400"
      : variant === "warn"
        ? "text-amber-400"
        : "text-fg-subtle";
  return (
    <div className="flex justify-center px-4">
      <p
        className={`max-w-md text-center font-mono text-[11px] leading-relaxed ${color}`}
      >
        {children}
      </p>
    </div>
  );
}
